import { ESdChangeReason, ICommonSettings, IConfig } from "@kuika/kuika-cl-model";
import { connect } from "react-redux";
import { ConfigService } from "../../../services/config-service";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../shared/hoc/with-history";
import { withManagement } from "../../../shared/hoc/with-management";
import { PureSdComponent } from "../../../shared/pure-sd";
import { LoadingPresentationBase, resolveLoadingPresentation } from "../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import {
  createGlobalConfig,
  setActiveGlobalConfiguration,
  setGlobalConfigs,
  setGlobalConfigsLoadingStatus
} from "../../../store/global-config/actions";
import { AppState } from "../../../store/root-reducer";
import { DeleteModal } from "../../common/delete-modal";
import { ConfigManagerLanding } from "../../design-tools/app-settings/config-manager-landing";
import { ConfigNameDialog, ConfigNameDialogState } from "../../design-tools/app-settings/config-name-dialog";
import { buildConfigRowActions } from "../../design-tools/app-settings/config-row-actions";
import { KuikaSkeletonForm, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import { GlobalConfigsContainer } from "./global-configs-container";

export interface IGlobalConfigsCanvasState {
  /** The row a delete has been requested for; the confirmation is owned here. */
  configToDelete?: IConfig;
  /** The name sheet the editor switcher's Rename opened. */
  nameDialog: ConfigNameDialogState;
  /**
   * The screen's first fetch has answered. `isGlobalConfigsLoading` alone cannot say so: every
   * rename, delete and settings save raises it too, and a workspace with no configurations
   * yet has an empty list either way.
   */
  hasLoadedConfigs: boolean;
}

export interface IGlobalConfigsCanvasProps {
  activeGlobalConfiguration?: IConfig;
  globalConfigs: IConfig[];
  isGlobalConfigsLoading: boolean;
  createGlobalConfig: (name: string) => void;
  setActiveGlobalConfiguration: (config?: IConfig) => void;
  setGlobalConfigs: (globalConfigs: IConfig[], globalCommonSettings: ICommonSettings) => void;
  setGlobalConfigsLoadingStatus: (isGlobalConfigsLoading: boolean) => void;
}

/**
 * Global Configurations is the Config Manager at workspace scope: the same entity, the same
 * editor, the same landing page — only the store behind it differs (redux and
 * `/Configuration/domain/*` rather than the screen-design context and
 * `/Configuration/project/*`).
 *
 * The landing replaced this screen's own left panel, which was the last un-migrated copy of
 * a list the project side had already rebuilt: inline hex, a kebab left in the DOM at
 * `opacity: 0` and still clickable, `stroke="black"`, a rename and a duplicate that had been
 * commented out for long enough that `duplicate()` no longer existed, and no empty state at
 * all. Delete was the one thing it really did, and it lives here now — with the host, so the
 * confirmation survives the row disappearing under it.
 */
class GlobalConfigsCanvas extends PureSdComponent<
  IGlobalConfigsCanvasProps & IWithContextProp & IWithHistoryProp,
  IGlobalConfigsCanvasState
> {
  constructor(props: IGlobalConfigsCanvasProps & IWithContextProp & IWithHistoryProp) {
    super(props);
    this.state = { configToDelete: undefined, nameDialog: { kind: "closed" }, hasLoadedConfigs: false };
  }

  componentDidMount(): void {
    void this.fetchConfigs();
  }

  fetchConfigs = async (): Promise<void> => {
    this.props.setGlobalConfigsLoadingStatus(true);
    try {
      const {
        data: { configurations, commonSettings }
      } = await ConfigService.getGlobalConfigs();
      this.props.setGlobalConfigs(configurations, commonSettings);
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.props.setGlobalConfigsLoadingStatus(false);
      this.setState({ hasLoadedConfigs: true });
    }
  };

  /**
   * `updateGlobalConfig` PUTs the whole document, so the caller must hand back the FULL
   * `IConfig` with just `name` replaced — `config` here is one of the rows this canvas
   * already holds in full, never a partial fetched just for the rename.
   */
  handleRenameConfig = async (config: IConfig, name: string): Promise<boolean> => {
    try {
      await ConfigService.updateGlobalConfig({ ...config, name });
      // No "has been updated" toast: the rename sheet holds its confirm's ✓, closes, and the list
      // lights the row under its new name.
      await this.fetchConfigs();
      return true;
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      // Rethrown so the list's dialog knows the write did not land — it stays open and
      // stops its spinner rather than reporting success it cannot back up.
      throw error;
    }
  };

  handleDeleteConfig = async (): Promise<void> => {
    const target: IConfig | undefined = this.state.configToDelete;
    if (!target?.id) return;

    this.props.setGlobalConfigsLoadingStatus(true);
    try {
      await ConfigService.deleteGlobalConfig(target.id);
      // Deleting the open configuration has to close it, or the editor keeps writing to an
      // id the backend no longer has.
      if (this.props.activeGlobalConfiguration?.id === target.id) {
        this.props.setActiveGlobalConfiguration();
      }
      KMainFunctions.displaySuccessNotification("Configuration has been deleted successfully");
      await this.fetchConfigs();
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.props.setGlobalConfigsLoadingStatus(false);
    }
  };

  renderContent = (): React.ReactNode => {
    if (!this.props.activeGlobalConfiguration) {
      return (
        <ConfigManagerLanding
          scope="global"
          configs={this.props.globalConfigs}
          onOpenConfig={(config: IConfig) => this.props.setActiveGlobalConfiguration(config)}
          onCreateConfig={(name: string) => this.props.createGlobalConfig(name)}
          onRenameConfig={this.handleRenameConfig}
          onRequestDelete={(config: IConfig) => this.setState({ configToDelete: config })}
        />
      );
    }

    return (
      <GlobalConfigsContainer
        rowActions={buildConfigRowActions({
          scope: "global",
          // A workspace list has no read-only references, so there is nothing to start from.
          onCreateFromGlobal: (): void => undefined,
          onRename: (row: IConfig) => this.setState({ nameDialog: { kind: "rename", row } }),
          onRequestDelete: (config: IConfig) => this.setState({ configToDelete: config })
        })}
      />
    );
  };

  /**
   * What the page lock draws: before the first fetch has answered, the form's outline; after it —
   * or with rows kept from an earlier visit, or an editor open, which must never be unmounted from
   * under its unsaved form — the content stays, dimmed and held still until the refetch is back.
   *
   * The landing is not handed the flag as well: this lock already covers its list, and a table
   * overlay under the canvas's would dim the rows twice.
   */
  getLoadingPresentation = (): LoadingPresentationBase => {
    const hasData: boolean =
      this.state.hasLoadedConfigs ||
      !!this.props.activeGlobalConfiguration ||
      (this.props.globalConfigs?.length ?? 0) > 0;
    return resolveLoadingPresentation(this.props.isGlobalConfigsLoading, hasData);
  };

  render() {
    const presentation: LoadingPresentationBase = this.getLoadingPresentation();

    return (
      // `globalConfigurationsManager` is the screen's readiness anchor for E2E: unlike a
      // title it exists in both the empty and the configured state (KOA-15840).
      <div className="kuika-global-configs" data-testid="globalConfigurationsManager">
        {presentation === "skeleton" ? (
          <KuikaSkeletonRegion label="Loading global configurations" className="kuika-config-canvas__skeleton">
            <KuikaSkeletonForm />
          </KuikaSkeletonRegion>
        ) : (
          <KuikaSpinnerSmall spinning={presentation === "refreshing"}>{this.renderContent()}</KuikaSpinnerSmall>
        )}
        {/* The editor switcher's Rename lands here: the landing owns its own sheet, but it is
            not mounted while a configuration is open. */}
        <ConfigNameDialog
          scope="global"
          configs={this.props.globalConfigs ?? []}
          dialog={this.state.nameDialog}
          onClose={() => this.setState({ nameDialog: { kind: "closed" } })}
          onCreateConfig={(name: string) => this.props.createGlobalConfig(name)}
          onRenameConfig={this.handleRenameConfig}
        />
        <DeleteModal
          itemName={this.state.configToDelete?.name ?? ""}
          triggerModal={!!this.state.configToDelete}
          deleteItem={() => void this.handleDeleteConfig()}
          deleteType={"Configuration"}
          closeModal={() => this.setState({ configToDelete: undefined })}
        />
      </div>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  activeGlobalConfiguration: state.globalConfig.activeGlobalConfiguration,
  globalConfigs: state.globalConfig.globalConfigs,
  isGlobalConfigsLoading: state.globalConfig.isGlobalConfigsLoading
});

const mapDispatchToProps = (dispatch) => ({
  createGlobalConfig: (name: string) => dispatch(createGlobalConfig(name)),
  setActiveGlobalConfiguration: (config?: IConfig) => dispatch(setActiveGlobalConfiguration(config)),
  setGlobalConfigs: (globalConfigs: IConfig[], globalCommonSettings: ICommonSettings) =>
    dispatch(setGlobalConfigs(globalConfigs, globalCommonSettings)),
  setGlobalConfigsLoadingStatus: (isGlobalConfigsLoading: boolean) =>
    dispatch(setGlobalConfigsLoadingStatus(isGlobalConfigsLoading))
});

const globalConfigsCanvas = connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withContext<IGlobalConfigsCanvasProps>(
    withHistory<IGlobalConfigsCanvasProps>(withManagement<IGlobalConfigsCanvasProps>(GlobalConfigsCanvas))
  )
);
export { globalConfigsCanvas as GlobalConfigsCanvas };
